import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListFilterSelect, ListPagination,
} from '../components/ui/ListPageChrome';
import { useTableSort, SortTh } from '../components/ui/TableSort';

const STATUS_COLORS = {
  Draft:     'bg-[#F0F4F3] text-[#6B7F7F]',
  Open:      'bg-[#E8F4FE] text-[#2184F2]',
  Closed:    'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};

const fmt = (v) => v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function SalesOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  // Sorting sits on top of whatever the page already loaded.

  const [rows, setRows]       = useState([]);
  const sort = useTableSort(rows);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const limit = 25;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit });
        if (search) params.set('search', search);
        if (statusFilter) params.set('status', statusFilter);
        const res = await api.get(`/sales-orders?${params}`);
        if (!cancelled) {
          setRows(res?.rows || []);
          setTotal(res?.total || 0);
          setSelectedIds(new Set());
          setIsAllSelected(false);
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, search, statusFilter, refreshKey]);

  const totalPages = Math.ceil(total / limit) || 1;
  const resetPage = () => setPage(1);

  const handleSelectRow = (id) => {
    if (isAllSelected) {
      const next = new Set();
      rows.forEach(r => {
        if (r.so_id !== id) {
          next.add(r.so_id);
        }
      });
      setSelectedIds(next);
      setIsAllSelected(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const pageAllSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.so_id));
    if (isAllSelected || pageAllSelected) {
      setIsAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setIsAllSelected(true);
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = isAllSelected ? total : selectedIds.size;
    if (count === 0) return;
    const msg = `Are you sure you want to delete the ${count} selected sales order(s)? This will also delete their associated lines and attachments.`;
    if (!window.confirm(msg)) return;

    try {
      if (isAllSelected) {
        await api.post('/sales-orders/bulk-delete', {
          all: true,
          search: search || undefined,
          status: statusFilter || undefined
        });
      } else {
        await api.post('/sales-orders/bulk-delete', {
          ids: Array.from(selectedIds)
        });
      }
      setSelectedIds(new Set());
      setIsAllSelected(false);
      
      const newTotal = total - count;
      const newMaxPages = Math.ceil(newTotal / limit) || 1;
      if (page > newMaxPages) {
        setPage(newMaxPages);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete sales orders');
    }
  };

  const displayCount = isAllSelected ? total : selectedIds.size;

  return (
    <ListPageShell>
      <ListPageHeader
        title="Sales Orders"
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && displayCount > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Delete Selected ({displayCount})
              </button>
            )}
            <button type="button" onClick={() => navigate('/sales-order/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 items-center">
        <ListSearchBar
          value={search}
          onChange={setSearch}
          onChangeExtra={resetPage}
          placeholder="Search SO number or customer…"
        />
        <ListFilterSelect value={statusFilter} onChange={setStatusFilter} onChangeExtra={resetPage}>
          <option value="">All Status</option>
          {['Draft','Open','Closed','Cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </ListFilterSelect>
      </div>

      <div className="data-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {isSuperAdmin && (
                <th className="text-center px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected || (rows.length > 0 && rows.every(r => selectedIds.has(r.so_id)))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                  />
                </th>
              )}
              <SortTh sort={sort} field="so_number" className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="left">SO Number</SortTh>
              <SortTh sort={sort} field="customer_name" className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="left">Customer</SortTh>
              <SortTh sort={sort} field="posting_date" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Posting Date</SortTh>
              <SortTh sort={sort} field="delivery_date" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Delivery Date</SortTh>
              <SortTh sort={sort} field="status" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Status</SortTh>
              <SortTh sort={sort} field="subtotal" className="text-right px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="right">Subtotal</SortTh>
              <SortTh sort={sort} field="line_count" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Lines</SortTh>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? "8" : "7"} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? "8" : "7"} className="px-4 py-8 text-center text-sap-textLight">
                No sales orders found. Click "+ Add New" to create one.
              </td></tr>
            ) : sort.rows.map((r) => (
              <tr key={r.so_id}
                onClick={() => navigate(`/sales-order/${r.so_id}`)}
                className="table-row even:bg-gray-50/50">
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isAllSelected || selectedIds.has(r.so_id)}
                      onChange={() => handleSelectRow(r.so_id)}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-sap-blue">{r.so_number}</td>
                <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{fmtDate(r.posting_date)}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{fmtDate(r.delivery_date)}</td>
                <td className="px-4 py-3 text-center">
                  {r.status === 'Open' ? (
                    <span className="badge-active">{r.status}</span>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${STATUS_COLORS[r.status] || ''}`}>
                      {r.status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-semibold">{fmt(r.subtotal ?? r.doc_total)}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{r.line_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
    </ListPageShell>
  );
}
